import React, { useState } from 'react';
import {
  Button,
  Input,
  Spinner,
  Text,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  TableCellLayout,
  Tooltip,
  ProgressBar,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  ArrowClockwise24Regular,
  OpenRegular,
  AddCircle24Regular,
  Warning24Regular,
} from '@fluentui/react-icons';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { BoondProject } from '@gridwise/shared';
import { getProjects } from '../api/projects';
import StatusBadge from '../components/StatusBadge';
import PageHeader from '../components/PageHeader';
import EmptyState from '../components/EmptyState';

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    background: tokens.colorNeutralBackground2,
  },
  toolbar: {
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalXL}`,
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    alignItems: 'center',
    background: tokens.colorNeutralBackground1,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  tableContainer: {
    flex: 1,
    overflow: 'auto',
    padding: tokens.spacingHorizontalXL,
  },
  tableWrapper: {
    background: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    overflow: 'hidden',
  },
  actionsCell: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
    alignItems: 'center',
  },
  chargeCell: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    minWidth: '120px',
  },
  amount: { fontVariantNumeric: 'tabular-nums' },
});

export default function MissionsActives() {
  const styles = useStyles();
  const [search, setSearch] = useState('');

  const { data, isLoading, isError, isFetching, refetch } = useQuery({
    queryKey: ['projects', 'active', search],
    queryFn: () => getProjects({ search, status: '1' }),
  });

  const projects: BoondProject[] = data?.data || [];

  const formatDate = (d?: string) => {
    if (!d) return '—';
    try {
      return format(new Date(d), 'dd/MM/yyyy', { locale: fr });
    } catch {
      return d;
    }
  };

  const getChargePercent = (p: BoondProject) => {
    if (!p.plannedCharge || p.plannedCharge === 0) return 0;
    return Math.min(100, Math.round(((p.consumedCharge || 0) / p.plannedCharge) * 100));
  };

  const formatDays = (d?: number) => (d != null ? `${d}j` : '—');

  return (
    <div className={styles.page}>
      <PageHeader
        title="Missions actives"
        subtitle="Suivi des projets et prestations en cours"
        actions={
          <Button
            icon={<ArrowClockwise24Regular />}
            appearance="subtle"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            Actualiser
          </Button>
        }
      />

      <div className={styles.toolbar}>
        <Input
          placeholder="Rechercher un projet, client..."
          value={search}
          onChange={(_, d) => setSearch(d.value)}
          style={{ width: 280 }}
        />
        <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
          {projects.length} mission{projects.length !== 1 ? 's' : ''} active{projects.length !== 1 ? 's' : ''}
        </Text>
      </div>

      <div className={styles.tableContainer}>
        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
            <Spinner label="Chargement des missions..." />
          </div>
        ) : isError ? (
          <EmptyState
            title="Erreur de chargement"
            description="Impossible de récupérer les données Boond."
          />
        ) : projects.length === 0 ? (
          <EmptyState
            title="Aucune mission active"
            description="Aucun projet actif trouvé dans Boond."
          />
        ) : (
          <div className={styles.tableWrapper}>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>Client</TableHeaderCell>
                  <TableHeaderCell>Mission / Projet</TableHeaderCell>
                  <TableHeaderCell>Responsable</TableHeaderCell>
                  <TableHeaderCell>Statut</TableHeaderCell>
                  <TableHeaderCell>Période</TableHeaderCell>
                  <TableHeaderCell>Charge</TableHeaderCell>
                  <TableHeaderCell>RAF / RAF€</TableHeaderCell>
                  <TableHeaderCell>Risque</TableHeaderCell>
                  <TableHeaderCell>Actions</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {projects.map((proj) => {
                  const pct = getChargePercent(proj);
                  return (
                    <TableRow key={proj.id}>
                      <TableCell>
                        <TableCellLayout>
                          <Text size={300} weight="semibold">{proj.client?.name || '—'}</Text>
                        </TableCellLayout>
                      </TableCell>
                      <TableCell>
                        <TableCellLayout>
                          <Text size={300}>{proj.title}</Text>
                          {proj.reference && (
                            <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                              {' '}#{proj.reference}
                            </Text>
                          )}
                        </TableCellLayout>
                      </TableCell>
                      <TableCell>
                        <Text size={300}>{proj.manager || '—'}</Text>
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={proj.status} type="opportunity" />
                      </TableCell>
                      <TableCell>
                        <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                          {formatDate(proj.startDate)} → {formatDate(proj.endDate)}
                        </Text>
                      </TableCell>
                      <TableCell>
                        <div className={styles.chargeCell}>
                          <Text size={200}>
                            {formatDays(proj.consumedCharge)} / {formatDays(proj.plannedCharge)} ({pct}%)
                          </Text>
                          <ProgressBar
                            value={pct / 100}
                            color={pct >= 100 ? 'error' : pct >= 80 ? 'warning' : 'success'}
                            thickness="medium"
                          />
                        </div>
                      </TableCell>
                      <TableCell>
                        <Text size={300} className={styles.amount}>
                          {formatDays(proj.remainingToProduce)}
                          {proj.remainingToBill != null && (
                            <Text size={200} style={{ color: tokens.colorNeutralForeground3, display: 'block' }}>
                              {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(proj.remainingToBill)}
                            </Text>
                          )}
                        </Text>
                      </TableCell>
                      <TableCell>
                        {proj.riskLevel ? (
                          <StatusBadge status={proj.riskLevel} type="risk" />
                        ) : (
                          <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>—</Text>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className={styles.actionsCell}>
                          <Tooltip content="Ouvrir dans Boond" relationship="label">
                            <Button
                              icon={<OpenRegular />}
                              appearance="subtle"
                              size="small"
                              as="a"
                              href={`${import.meta.env.VITE_BOOND_APP_URL || '#'}/project/${proj.id}`}
                              target="_blank"
                            />
                          </Tooltip>
                          <Tooltip content="Créer une action" relationship="label">
                            <Button
                              icon={<AddCircle24Regular />}
                              appearance="subtle"
                              size="small"
                              onClick={() => {
                                window.open(
                                  `/task/envoyer-vers-boond?entityType=project&entityId=${proj.id}&entityLabel=${encodeURIComponent(proj.title)}`,
                                  '_blank',
                                  'width=600,height=700',
                                );
                              }}
                            />
                          </Tooltip>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
